import { assert, assertNever, isDef } from '~/util';
import { waveformsDuration } from './waveform';
import type {
  Basis,
  Channel,
  Device,
  Pulse,
  StudioExperiment
} from '~/models/studio';
import type { Bounds } from '~/util';

export interface PulseLayout {
  time: Bounds;
  phaseOffset: number;
}

/**
 * Create a closure for building the pulse layout. The building is happening one pulse at a time.
 *
 * Maintains state of the builder in progress, as well as allows taking a state snapshot and
 * restoring it later. This is useful when trying to find the best layout for given external criteria.
 */
export function layoutBuilder(device: Device, channels: readonly Channel[]) {
  let st = {
    layout: {} as Record<string, PulseLayout>,
    processedPulses: [] as Pulse[],
    // last pulse for each channel
    lastChannelPulse: new Map<string, Pulse>(),
    // last OpTarget time for each channel
    lastRetargetTime: new Map<string, number>(),
    // phase reference for each atom/basis combo
    phaseRef: new Map<string, number>()
  };

  return {
    saveCheckpoint(): typeof st {
      return {
        layout: { ...st.layout },
        processedPulses: [...st.processedPulses],
        lastChannelPulse: new Map(st.lastChannelPulse),
        lastRetargetTime: new Map(st.lastRetargetTime),
        phaseRef: new Map(st.phaseRef)
      };
    },
    loadCheckpoint(checkpoint: typeof st) {
      st = {
        layout: { ...checkpoint.layout },
        processedPulses: [...checkpoint.processedPulses],
        lastChannelPulse: new Map(checkpoint.lastChannelPulse),
        lastRetargetTime: new Map(checkpoint.lastRetargetTime),
        phaseRef: new Map(checkpoint.phaseRef)
      };
    },
    getLayouts() {
      return st.layout;
    },
    processedPulses() {
      return st.processedPulses;
    },
    /**
     * Core method for calculating the layout.
     *
     * Processes each pulse in the sequence, mimics the layout algorithm from Pulser python library.
     */
    processPulse(pulse: Pulse): PulseLayout {
      const channel = channels.find((c) => c.uuid === pulse.channelUuid);
      assert(channel != null);

      const previousOnChannel = st.lastChannelPulse.get(channel.uuid);
      st.lastChannelPulse.set(channel.uuid, pulse);

      const global = channel.addressing === 'global';
      assert(
        global === (pulse.targetUuid === null),
        'addressing mode must be compatible with specified pulse target'
      );

      const basis = channel.basis;

      const duration = Math.max(channel.minDuration, pulseDuration(pulse));

      const isChangingTarget =
        previousOnChannel != null &&
        pulse.targetUuid !== previousOnChannel.targetUuid;

      const nextChannelTimeslot =
        previousOnChannel != null
          ? st.layout[previousOnChannel.uuid].time.max + 1
          : 0;
      let pulseStartTime = nextChannelTimeslot;
      pulseStartTime += pulse.delay;

      if (isChangingTarget) {
        // OpTarget
        const lastRetarget = st.lastRetargetTime.get(channel.uuid) ?? 0;
        const retargetTime = Math.max(
          lastRetarget + (channel.minRetargetInterval ?? 0),
          nextChannelTimeslot + (channel.fixedRetargetT ?? 0)
        );
        st.lastRetargetTime.set(channel.uuid, retargetTime);
        pulseStartTime = Math.max(pulseStartTime, retargetTime);
      }

      // OpPulse

      // phase refs
      let phaseBase = 0;
      if (pulse.targetUuid != null) {
        const phaseKey = phaseRefKey(pulse.targetUuid, basis);
        phaseBase = st.phaseRef.get(phaseKey) ?? 0;
        if (pulse.postPhaseShift !== 0) {
          st.phaseRef.set(phaseKey, phaseBase + pulse.postPhaseShift);
        }
      }

      // consider phase change time
      if (previousOnChannel != null) {
        const previousTotalPhase =
          previousOnChannel.phase +
          st.layout[previousOnChannel.uuid].phaseOffset;
        const totalPhase = pulse.phase + phaseBase;
        if (previousTotalPhase !== totalPhase) {
          const phaseChangeFinishTime =
            st.layout[previousOnChannel.uuid].time.max +
            1 +
            channel.phaseJumpTime;
          pulseStartTime = Math.max(pulseStartTime, phaseChangeFinishTime);
        }
      }

      // move pulse to next available timeslot, based on protocol

      // first, consider explicitly defined dependencies
      const dependencies = pulse.dependencies
        .map((uuid) => st.processedPulses.find((p) => p.uuid === uuid))
        .filter(isDef);
      // then, add implciit dependencies based on addressing mode
      // (don't bother with deduplication, as the conflict resolution is idempotent)
      switch (pulse.protocol) {
        case 'no-delay':
          break; // do nothing
        case 'min-delay':
          dependencies.push(
            ...st.processedPulses.filter(
              (p) =>
                p.channelUuid !== channel.uuid &&
                targetConflicts(p.targetUuid, pulse.targetUuid)
            )
          );
          break;
        case 'wait-for-all':
          dependencies.push(
            ...st.processedPulses.filter((p) => p.channelUuid !== channel.uuid)
          );
          break;
        default:
          assertNever(pulse.protocol);
      }

      for (const conflict of dependencies) {
        pulseStartTime = Math.max(
          pulseStartTime,
          st.layout[conflict.uuid].time.max + 1
        );
      }

      st.processedPulses.push(pulse);
      return (st.layout[pulse.uuid] = {
        time: { min: pulseStartTime, max: pulseStartTime + duration - 1 },
        phaseOffset: phaseBase
      });
    }
  };
}

/**
 * Calculate the layout (absolute positions and phases) of pulses.
 */
export function computeLayout(
  state: Readonly<StudioExperiment>
): Record<string, PulseLayout> {
  const builder = layoutBuilder(state.device, state.channels);
  for (const pulse of state.pulses) {
    builder.processPulse(pulse);
  }
  return builder.getLayouts();
}

/**
 * Get the total duration of a pulse.
 *
 * It is the maximum of the pulse amplitude waveform and detuning waveform durations.
 */
export function pulseDuration(pulse: Readonly<Pulse>): number {
  return Math.max(
    waveformsDuration(pulse.amplitude),
    pulse.detuning ? waveformsDuration(pulse.detuning) : 0
  );
}

function targetConflicts(
  selfTarget: string | null,
  otherTarget: string | null
): boolean {
  return (
    selfTarget === otherTarget || selfTarget == null || otherTarget == null
  );
}

function phaseRefKey(targetUuid: string, basis: Basis): string {
  return `${targetUuid}:${basis}`;
}
